import { BlueprintType } from '@constants'
import { IAccountingSuggestionUIRow } from '@query/suggestions'
import { network } from '@utils'
import { IFile } from './files'
import {
  ActionFunction,
  handleReturn,
  MutationFunction,
  PaginatedResponse,
  QueryFunction
} from './root'
import {
  QueryKey,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query'
import { useBusinessContext } from '@root/context/BusinessContext'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import {
  IDocumentRelation,
  IRelationEntrySignals,
  IRelationLinkKindMeta,
  IRelationNetworkSignals
} from './documentRelations'

export enum ImportDataSource {
  BANK_INTEGRATION = 'BANK',
  CSV = 'CSV',
  HOLVI = 'HOLVI',
  SALES_INVOICE = 'SALES_INVOICE',
  PURCHASE_INVOICE = 'PURCHASE_INVOICE',
  SALAXY = 'SALAXY',
  WORKFLOW = 'WORKFLOW'
}

export const bankIntegrationDataSources = [
  ImportDataSource.BANK_INTEGRATION,
  ImportDataSource.HOLVI,
  ImportDataSource.CSV,
  ImportDataSource.SALES_INVOICE,
  ImportDataSource.PURCHASE_INVOICE
]

export const getImportSourceLabelKey = (source: ImportDataSource): string => {
  const map: Record<ImportDataSource, string> = {
    [ImportDataSource.BANK_INTEGRATION]: 'bankTransaction',
    [ImportDataSource.HOLVI]: 'bankTransaction',
    [ImportDataSource.CSV]: 'csvImport',
    [ImportDataSource.SALES_INVOICE]: 'salesInvoice',
    [ImportDataSource.PURCHASE_INVOICE]: 'purchaseInvoice',
    [ImportDataSource.SALAXY]: 'salaxy',
    [ImportDataSource.WORKFLOW]: 'workflow'
  }
  return map[source] ?? 'bankTransaction'
}

export interface IPendingWorkflowRun {
  id: number
  workflow_id: number
  workflow_name: string
  status: 'pending' | 'running'
}

export interface IImportData {
  id: number
  trace_id: string
  identifier: string
  amount: number
  date: string
  description: string
  contact_hint: string
  reference_number?: string
  payment_account_id: number
  balance_after_transaction?: number
  data_source: ImportDataSource
  algo_status: string
  pending_workflow_runs?: IPendingWorkflowRun[]
}

export interface IBlueprintEntry {
  account_id?: number
  description?: string
  vat_code?: number
  vat_rate?: number
  vat_method?: string
  amount: number
  source_account_number?: string
}

export interface IBlueprint {
  debet_account_id?: number
  debet_entries?: IBlueprintEntry[]
  credit_account_id?: number
  credit_entries?: IBlueprintEntry[]
  expense_entries?: IBlueprintEntry[]
}

export enum IDocumentShortcutKey {
  CREATE_SETTLEMENT = 'create_settlement'
}

export interface IDocumentShortcut {
  key: IDocumentShortcutKey
  is_promoted: boolean
}

/** Payload from GET /document_similarity/ (copy-from-document search). */
export interface IDocumentSimilarity {
  id: number
  number: string
  description: string
  contact_id: number | null
  tag_ids: number[]
  balance: number
  blueprint: IBlueprint
  blueprint_type: BlueprintType
}

export type BankBalanceWarningReason = 'balance_mismatch'

export interface IBankBalanceWarning {
  reason: BankBalanceWarningReason
  diff: number | null
  bank_balance: number | null
  accounting_balance: number | null
}

export interface IDocument {
  id: number
  date: string
  contact_id: number
  contact_name: string
  balance: number
  number: string
  description: string
  is_flagged: boolean
  is_locked: boolean
  is_draft: boolean
  attachment_exempt: boolean
  is_deletable?: boolean
  type: number
  updated_at: string
  created_at: string
  blueprint: IBlueprint
  blueprint_type: BlueprintType
  attachment_ids: number[]
  import_data?: IImportData
  relation_network_signals?: IRelationNetworkSignals
  relation_entry_signals?: IRelationEntrySignals
  available_link_kinds?: IRelationLinkKindMeta[]
  entry_ui_rows: IAccountingSuggestionUIRow[]
  audit_trail_last_seen_at: string | null
  audit_trail_last_mention_at: string | null
  audit_trail_last_comment_at: string | null
  audit_trail_has_unseen_actions: boolean
  tag_ids: number[]
  relations: IDocumentRelation[]
  shortcuts?: IDocumentShortcut[]
  eu_b2b_vat_details_missing: boolean
  eu_sale_to_private_person: boolean
  bank_balance_warning: IBankBalanceWarning | null
}

interface DocumentListProps {
  slug: string
}

interface DocumentInstanceProps extends DocumentListProps {
  documentId: number
}

export const fetchDocuments: QueryFunction<
  DocumentListProps,
  PaginatedResponse<IDocument>
> = async ({ slug }, params = null) => {
  const url = `/v1/business/${slug}/document/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export const createDocument: MutationFunction<DocumentListProps, IDocument> = async (
  { slug },
  requestData
) => {
  const url = `/v1/business/${slug}/document/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const fetchDocument: QueryFunction<DocumentInstanceProps, IDocument> = async (
  { slug, documentId },
  params = null
) => {
  const url = `/v1/business/${slug}/document/${documentId}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export const updateDocument: MutationFunction<DocumentInstanceProps, IDocument> = async (
  { slug, documentId },
  requestData
) => {
  const url = `/v1/business/${slug}/document/${documentId}/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'PATCH',
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const deleteDocument: MutationFunction<DocumentInstanceProps, void> = async (
  { slug, documentId },
  params = null
) => {
  const url = `/v1/business/${slug}/document/${documentId}/`
  await network.httpClient.request({
    url,
    method: 'DELETE',
    params
  })
}

interface IMassDeleteProps {
  id: number
}

export interface IMassDeleteResult {
  delete: number
  locked: number
}

export const massDeleteDocuments: ActionFunction<
  DocumentListProps,
  IMassDeleteProps[],
  IMassDeleteResult
> = async ({ slug }, requestData) => {
  const url = `/v1/business/${slug}/document/actions/delete/`
  const { data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: requestData
  })

  return data
}

// ACTIONS

export const lockDocument: MutationFunction<DocumentInstanceProps, void> = async (
  { slug, documentId },
  params = null
) => {
  const url = `/v1/business/${slug}/document/${documentId}/action/lock/`
  const { data } = await network.httpClient.request({
    url,
    method: 'POST',
    params
  })
  return data
}

export const unlockDocument: MutationFunction<DocumentInstanceProps, void> = async (
  { slug, documentId },
  params = null
) => {
  const url = `/v1/business/${slug}/document/${documentId}/action/unlock/`
  const { data } = await network.httpClient.request({
    url,
    method: 'POST',
    params
  })
  return data
}

interface DocumentCopyProps extends DocumentInstanceProps {
  sourceId: number
}

export const copyDocument: MutationFunction<DocumentCopyProps, void> = async ({
  slug,

  documentId,
  sourceId
}) => {
  const url = `/v1/business/${slug}/document/${documentId}/action/copy/`
  const { data } = await network.httpClient.request({
    url,
    method: 'POST',
    params: { source: sourceId }
  })
  return data
}

export const attachFiles: MutationFunction<DocumentInstanceProps, number[]> = async (
  { slug, documentId },
  fileIds
) => {
  const url = `/v1/business/${slug}/document/${documentId}/action/attach_files/`
  const { data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: fileIds
  })
  return data
}

export const detachFiles: MutationFunction<DocumentInstanceProps, number[]> = async (
  { slug, documentId },
  fileIds
) => {
  const url = `/v1/business/${slug}/document/${documentId}/action/detach_files/`
  const { data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: fileIds
  })
  return data
}

export const setFiles: MutationFunction<DocumentInstanceProps, number[]> = async (
  { slug, documentId },
  fileIds
) => {
  const url = `/v1/business/${slug}/document/${documentId}/action/set_files/`
  const { data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: fileIds
  })
  return data
}

export const flagDocument: MutationFunction<DocumentInstanceProps, void> = async (
  { slug, documentId },
  params = null
) => {
  const url = `/v1/business/${slug}/document/${documentId}/action/flag/`
  const { data } = await network.httpClient.request({
    url,
    method: 'POST',
    params
  })
  return data
}

export const setAttachmentExempt: MutationFunction<DocumentInstanceProps, boolean> = async (
  { slug, documentId },
  attachmentExempt
) => {
  const action = attachmentExempt ? 'mark_attachment_exempt' : 'require_attachments'
  const url = `/v1/business/${slug}/document/${documentId}/action/${action}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'POST'
  })
  return data
}

export const unflagDocument: MutationFunction<DocumentInstanceProps, void> = async (
  { slug, documentId },
  params = null
) => {
  const url = `/v1/business/${slug}/document/${documentId}/action/unflag/`
  const { data } = await network.httpClient.request({
    url,
    method: 'POST',
    params
  })
  return data
}

interface SimilarityProps {
  slug: string
  search: string
  importModelId?: number
}

export const fetchSimilarDocuments: QueryFunction<SimilarityProps, IDocumentSimilarity[]> = async ({
  slug,
  search,
  importModelId
}) => {
  const url = `/v1/business/${slug}/document_similarity/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params: { query: search, import_model: importModelId }
  })
  return data
}

export const fetchSuggestedAttachments: QueryFunction<DocumentInstanceProps, IFile[]> = async ({
  slug,
  documentId
}) => {
  const url = `/v1/business/${slug}/document/${documentId}/suggest_attachments/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface IDocumentLimits {
  usage_percentage: number
  used: number
  limit: number
  remaining: number
}

export const fetchDocumentLimits: QueryFunction<DocumentListProps, IDocumentLimits> = async (
  { slug },
  { date }
) => {
  const url = `/v1/business/${slug}/document_limit/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params: { date }
  })
  return data
}

export interface ITrialLimit {
  limit: number | null
  used: number
  remaining: number | null
}

export interface ITrialLimits {
  is_trialing: boolean
  trial_started_at: string | null
  trial_ends_at: string | null
  trial_days_remaining: number | null
  document_usage: ITrialLimit
  payg_invoice_usage: ITrialLimit & {
    is_enabled: boolean
  }
}

export const fetchTrialLimits: QueryFunction<DocumentListProps, ITrialLimits> = async ({
  slug
}) => {
  const url = `/v1/business/${slug}/trial_usage/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

const getStartOfMonthISO = (dateAt: Date | string) =>
  dayjs(dateAt).startOf('month').format('YYYY-MM-DD')

type DocumentLimitQueryOptions = Omit<
  UseQueryOptions<IDocumentLimits, unknown, IDocumentLimits, QueryKey>,
  'queryKey' | 'queryFn'
>

export const useDocumentLimits = (
  dateAt: Date | string,
  options: DocumentLimitQueryOptions = {}
): UseQueryResult<IDocumentLimits, unknown> => {
  const { slug } = useBusinessContext()
  const dateString = useMemo(() => getStartOfMonthISO(dateAt), [dateAt])

  return useQuery({
    queryKey: [slug, 'document_limits', dateString],
    queryFn: () => fetchDocumentLimits({ slug }, { date: dateString }),
    ...{
      refetchOnMount: false,
      ...options
    }
  })
}

export const useInvalidateDocumentLimits = () => {
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()

  return async (dateAt: Date | string) => {
    const dateString = getStartOfMonthISO(dateAt)
    await queryClient.invalidateQueries({ queryKey: [slug, 'document_limits', dateString] })
  }
}

type TrialLimitsQueryOptions = Omit<
  UseQueryOptions<ITrialLimits, unknown, ITrialLimits, QueryKey>,
  'queryKey' | 'queryFn'
>

export const useTrialLimits = (
  options: TrialLimitsQueryOptions = {}
): UseQueryResult<ITrialLimits, unknown> => {
  const { slug } = useBusinessContext()

  return useQuery({
    queryKey: [slug, 'trial_limits'],
    queryFn: () => fetchTrialLimits({ slug }),
    ...{
      refetchOnMount: false,
      ...options
    }
  })
}

export const useInvalidateTrialLimits = () => {
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()

  return async () => {
    await queryClient.invalidateQueries({ queryKey: [slug, 'trial_limits'] })
  }
}

export interface IPaymentDocumentShortcutRequest {
  date: string
  amount: number
  attachment_ids: number[]
}

export type AIDocumentCreateStatus =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'FAILED'

export interface IAIDocumentCreateStartResponse {
  job_ids: string[]
}

export interface IAIDocumentCreateStatusResponse {
  job_id: string
  document_id: number | null
  transaction_id: number | null
  status: AIDocumentCreateStatus
  error_code?: string
  error_message?: string
}

export interface CreateDocumentsWithAIFromAttachmentsRequest {
  document_type: 'PURCHASE' | 'SALES'
  attachment_ids: number[]
}

interface DocumentAICreateProps {
  slug: string
}

export const createDocumentsWithAIFromAttachments = async (
  { slug }: DocumentAICreateProps,
  payload: CreateDocumentsWithAIFromAttachmentsRequest
): Promise<IAIDocumentCreateStartResponse> => {
  const url = `/v1/business/${slug}/document/ai_create/`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'POST',
    data: {
      document_type: payload.document_type,
      attachment_ids: payload.attachment_ids
    },
    validateStatus: () => true,
    timeout: 60000
  })
  return handleReturn({ status, data })
}

export interface IAIDocumentCreateJobStatus {
  job_id: string
  status: AIDocumentCreateStatus
  document_id: number | null
  error_message: string | null
}

export interface IAIDocumentCreateBatchStatusResponse {
  jobs: IAIDocumentCreateJobStatus[]
}

interface AIDocumentCreateBatchStatusProps extends DocumentAICreateProps {
  jobIds: string[]
}

export const fetchAIDocumentCreateBatchStatus: QueryFunction<
  AIDocumentCreateBatchStatusProps,
  IAIDocumentCreateBatchStatusResponse
> = async ({ slug, jobIds }) => {
  const jobIdsParam = jobIds.join(',')
  const url = `/v1/business/${slug}/document/ai_create/status/?job_ids=${encodeURIComponent(
    jobIdsParam
  )}`
  const { status, data } = await network.httpClient.request({
    url,
    method: 'GET',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}
